/* ==========================================================================
   Greasy Miniatures — legal and long-form page behaviour
   Loaded alongside greasy-legal.css by layout/theme.liquid on /policies/*
   and by sections/main-page.liquid on a page that opts in.

   This is the ONE file in the theme that builds markup, and the reason is
   structural rather than a shortcut: the document it has to navigate is
   either `page.content` or markup Shopify rendered before the layout ever
   saw it, and Liquid cannot see inside either. There is no heading list to
   render server-side because there is no way to FIND the headings
   server-side. So the contents list is built here, from the DOM, and the
   <details> that holds it ships hidden and empty — if this file never
   loads, the page is the styled document with no contents list, which is
   exactly what greasy-legal.css alone produces.

   Two jobs beyond that, both of them repairs to what Shopify hands over:

   - NORMALISING. A policy's body is buried two wrappers down, the
     generated privacy policy wraps its whole self in a bare <div>, and
     Refund and Terms each carry a second <h1> under Shopify's own title.
     See that section; all three were measured on the live documents.
   - PROMOTION. Shopify's policy editor CAN emit a clause title as
     `<p><strong>7. Your rights</strong></p>` — a paragraph that looks like
     a heading and is not one, so it gets no rule, no anchor and no place
     in the contents. None of this store's four policies is written that
     way today (they use real h2s), so this is insurance against the next
     policy someone pastes in, not something currently load-bearing.
   ========================================================================== */

(function () {
  'use strict';

  var DESKTOP = window.matchMedia('(min-width: 1024px)');

  /* A heading is short. These two bounds are what keep a wholly-bold
     PARAGRAPH — a legal emphasis block, which the policies do contain —
     from being mistaken for a clause title and hoisted into the contents
     list. Measured against the store's own copy. */
  var MAX_HEADING_CHARS = 90;
  var MAX_HEADING_WORDS = 14;

  /* Leading clause numbers are stripped from the contents label because the
     list numbers itself in CSS. "7. Your rights" would otherwise read
     "7. 7. Your rights".

     The optional SECTION/CLAUSE/PART/ARTICLE word is there for Shopify's
     own policy templates, which head every clause "SECTION 1 - WHAT DO WE
     DO WITH YOUR INFORMATION?" — that whole prefix is noise in a sidebar
     that is already numbering itself.

     Roman numerals are only stripped before a `.` or `)`, never before a
     dash. They have to be: [ivxlcdm]+ also spells real words, and "CIVIL -
     " or "MIX - " opening a clause would otherwise lose its first word. A
     digit is unambiguous, so a digit takes any separator. */
  var CLAUSE_NUMBER =
    /^\s*(?:(?:section|clause|part|article)\s+)?(?:\d+(?:\.\d+)*\s*[.)\]:–—-]|[ivxlcdm]+\s*[.)])\s*/i;

  /* ======================================================================
     PSEUDO-HEADING PROMOTION
     ====================================================================== */

  function looksLikeHeading(text) {
    if (!text) return false;
    if (text.length > MAX_HEADING_CHARS) return false;
    if (text.split(/\s+/).length > MAX_HEADING_WORDS) return false;
    /* A trailing full stop means a sentence. A question mark does NOT:
       Shopify's own policy templates head their clauses "SECTION 1 - WHAT
       DO WE DO WITH YOUR INFORMATION?" and the privacy one asks "How do we
       use your personal information?" — reject those and the two longest
       documents on the store keep every clause title as a bold paragraph,
       which is the exact problem this file exists to fix. A colon is fine
       too; "Contact us:" is a heading in half of these documents. */
    return !/[.!]$/.test(text);
  }

  /* `<p><strong>…</strong></p>`, including the `<p><span><strong>…` that
     comes out of a paste from Word. The test is that the paragraph has no
     text of its own outside the bold run — a bold lead-in followed by
     ordinary prose is a paragraph and must stay one. */
  function boldOnly(p) {
    var bold = p.querySelector('strong, b');
    if (!bold) return null;
    var whole = p.textContent.replace(/\s+/g, ' ').trim();
    var inner = bold.textContent.replace(/\s+/g, ' ').trim();
    return whole && whole === inner ? whole : null;
  }

  /* An all-capitals line on its own. The other shape the policies use, and
     the one no amount of CSS can find. Needs a real letter in it so a row
     of dashes or a bare "£" does not qualify. */
  function capsOnly(p) {
    var text = p.textContent.replace(/\s+/g, ' ').trim();
    if (!/[A-Z]/.test(text)) return null;
    if (/[a-z]/.test(text)) return null;
    return text;
  }

  function promote(root) {
    /* Direct children only. A bold line inside a list item or a table cell
       is not a clause title, and hoisting one out of an <li> would break
       the list it belongs to. */
    var paragraphs = Array.prototype.slice.call(root.children).filter(function (node) {
      return node.tagName === 'P';
    });

    paragraphs.forEach(function (p) {
      var text = boldOnly(p) || capsOnly(p);
      if (!text || !looksLikeHeading(text)) return;

      var heading = document.createElement('h2');
      heading.textContent = text;
      /* Marks what was inferred rather than authored, so a future reader of
         the rendered page can tell the two apart. */
      heading.setAttribute('data-gm-promoted', '');
      p.parentNode.replaceChild(heading, p);
    });
  }

  /* ======================================================================
     NORMALISING WHAT SHOPIFY HANDED US

     On /policies/* the document is not `page.content` — it is markup
     Shopify rendered before the layout ever saw it, and it arrives with
     three quirks that no stylesheet can fix. All three were found by
     measuring the four real policies on the store, not by reading docs.
     ====================================================================== */

  /* 1. The body is buried. `.shopify-policy__body .rte` is the document,
     two wrappers down from the element the layout could put a class on.
     This returns the real container so everything else works on it. */
  function documentRoot(body) {
    return body.querySelector('.shopify-policy__body .rte') || body;
  }

  /* 2. Shopify's generated PRIVACY policy wraps its entire body in a bare,
     class-less <div> — 39 children inside one wrapper, thirteen of them
     clause titles. Unwrapping it puts every document on this store at the
     same depth, which is what lets the contents list and the promotion
     below stop caring about nesting. Only a lone, attribute-free div is
     touched; anything with a class is somebody's deliberate wrapper. */
  function unwrap(root) {
    var only = root.children.length === 1 ? root.firstElementChild : null;
    if (!only) return root;
    if (only.tagName !== 'DIV' || only.attributes.length > 0) return root;

    while (only.firstChild) root.appendChild(only.firstChild);
    root.removeChild(only);
    return root;
  }

  /* 3. Refund and Terms each carry a SECOND <h1> inside the body — "Returns
     & Refunds Policy" sitting under Shopify's own "Refund policy" title —
     so the page shipped with two h1s and the title said twice. The layout
     renders the canonical heading from shop.policies, so this one becomes
     the h2 it was always behaving as. Only a LEADING h1 is demoted: an h1
     halfway down a document is a structure this has no business guessing
     at. */
  function demoteTitle(root) {
    var first = root.firstElementChild;
    if (!first || first.tagName !== 'H1') return;

    var heading = document.createElement('h2');
    heading.innerHTML = first.innerHTML;
    heading.setAttribute('data-gm-demoted', '');
    first.parentNode.replaceChild(heading, first);
  }

  /* "Last updated: July 9, 2026" opens the privacy policy. It is metadata
     wearing a paragraph, and the only way to know that is to read it.

     The first THREE children, not just the first: the privacy policy opens
     on its date line, but Terms puts the line under its own title, so
     checking only firstElementChild found one of the two and left the
     other as bold body copy. Three is enough for a title, a date and a
     lead, and short enough that a stray "Updated" paragraph deep in a
     document is never in range. */
  function markUpdated(root) {
    var candidates = Array.prototype.slice.call(root.children, 0, 3);

    for (var i = 0; i < candidates.length; i += 1) {
      var node = candidates[i];
      if (node.tagName !== 'P') continue;

      var text = node.textContent.replace(/\s+/g, ' ').trim();
      if (text.length > 60) continue;
      if (!/^(last\s+updated|effective(\s+date)?|updated)\b/i.test(text)) continue;

      node.setAttribute('data-gm-legal-updated', '');
      return;
    }
  }

  /* ======================================================================
     ANCHORS
     ====================================================================== */

  function slug(text) {
    var base = text
      .toLowerCase()
      .replace(CLAUSE_NUMBER, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
    return base ? 'gm-' + base.slice(0, 48) : 'gm-section';
  }

  function anchor(headings) {
    var used = Object.create(null);

    headings.forEach(function (heading) {
      /* An id already in the document wins — it may already be linked to
         from outside, and rewriting it would break that link. */
      if (heading.id) {
        used[heading.id] = true;
        return;
      }

      var base = slug(heading.textContent);
      var id = base;
      var n = 2;
      while (used[id] || document.getElementById(id)) {
        id = base + '-' + n;
        n += 1;
      }
      used[id] = true;
      heading.id = id;
    });
  }

  /* ======================================================================
     THE CONTENTS LIST
     ====================================================================== */

  function label(heading) {
    return heading.textContent.replace(/\s+/g, ' ').trim().replace(CLAUSE_NUMBER, '');
  }

  function build(mount, headings) {
    var list = document.createElement('ol');
    list.className = 'gm-legal__contents-list';
    list.setAttribute('role', 'list');

    var links = [];

    headings.forEach(function (heading) {
      var item = document.createElement('li');
      item.className = 'gm-legal__contents-item';
      /* H3 and below indent. Tested this way round rather than `!== 'H2'`
         because an h1 is also in the heading list now, and an h1 is the
         one thing that is certainly NOT a sub-clause. */
      if (heading.tagName !== 'H1' && heading.tagName !== 'H2') {
        item.className += ' gm-legal__contents-item--sub';
      }

      var link = document.createElement('a');
      link.className = 'gm-legal__contents-link';
      link.href = '#' + heading.id;
      link.textContent = label(heading);

      item.appendChild(link);
      list.appendChild(item);
      links.push({ link: link, heading: heading });
    });

    mount.appendChild(list);
    return links;
  }

  /* ======================================================================
     WHICH CLAUSE AM I IN
     Recomputed from geometry rather than tracked with an observer: a policy
     clause can be one line or three screens long, and the answer is always
     just "the last heading that has gone past the header".
     ====================================================================== */

  function headerOffset() {
    var raw = getComputedStyle(document.documentElement).getPropertyValue('--gm-header-height');
    var value = parseFloat(raw);
    return isNaN(value) ? 64 : value;
  }

  function track(links) {
    if (!links.length) return;

    var pending = false;

    function mark() {
      pending = false;
      /* 24 against greasy-base.css's scroll-padding-top of header + 16: a
         heading that a contents link just scrolled to sits 8px inside the
         threshold, so clicking an item always lights that item up. Any
         smaller and the click highlights the clause above the one you
         asked for. */
      var limit = headerOffset() + 24;
      var current = 0;

      for (var i = 0; i < links.length; i += 1) {
        if (links[i].heading.getBoundingClientRect().top - limit <= 0) {
          current = i;
        } else {
          break;
        }
      }

      links.forEach(function (entry, index) {
        if (index === current) {
          entry.link.setAttribute('aria-current', 'true');
        } else {
          entry.link.removeAttribute('aria-current');
        }
      });
    }

    function schedule() {
      if (pending) return;
      pending = true;
      window.requestAnimationFrame(mark);
    }

    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule, { passive: true });
    mark();
  }

  /* ======================================================================
     THE PANEL
     Same deal as the filter drawer in greasy-pages.js: the panel's state is
     owned by the breakpoint, because on desktop the summary is a caption
     with pointer-events off and a closed <details> there would be an
     unopenable sidebar.
     ====================================================================== */

  function syncPanel(panel) {
    return function () {
      if (DESKTOP.matches) panel.open = true;
    };
  }

  /* ======================================================================
     BOOT
     ====================================================================== */

  function init(scope) {
    var root = scope || document;
    var bodies = Array.prototype.slice.call(root.querySelectorAll('[data-gm-legal-body]'));

    bodies.forEach(function (body) {
      if (body.hasAttribute('data-gm-legal-ready')) return;
      body.setAttribute('data-gm-legal-ready', '');

      /* Order matters. Find the real container, flatten Shopify's wrapper,
         then read the first element — demoteTitle and markUpdated both ask
         "what does this document open with", and on the privacy policy the
         answer is only correct after the unwrap. */
      var doc = unwrap(documentRoot(body));
      demoteTitle(doc);
      markUpdated(doc);
      promote(doc);

      /* Marks the element that actually HOLDS the document, wherever it
         turned out to be — .gm-legal__prose on a page, Shopify's .rte four
         levels down on a policy. greasy-legal.css uses it for the one rule
         that genuinely needs to know the depth: suppressing the hairline
         above the first clause, which has nothing above it to divide from.
         Enumerating depths in the selector was tried and missed the policy
         case entirely. */
      doc.classList.add('gm-legal__doc');

      /* h1 is in the list because demoteTitle only reaches a LEADING one;
         an h1 further down stays an h1 and still belongs in the contents. */
      var headings = Array.prototype.slice.call(doc.querySelectorAll('h1, h2, h3'));
      if (!headings.length) return;

      anchor(headings);

      /* Scoped to this document's own layout, not to the page: a
         document.querySelector here fills the FIRST panel on the page
         whatever body it was reading, which is wrong the moment two of
         these sections share a page — as they do in the preview harness. */
      var layout = body.closest('.gm-legal__layout');
      if (!layout) return;

      /* The demoted title is anchored and styled like the rest, but it is
         NOT listed: a contents list names the parts of a document, and the
         document's own title is not one of them. Left in, the refund
         policy's list opened with "Returns & Refunds Policy" directly
         under a page heading that already said "Refund policy". */
      var indexable = headings.filter(function (heading) {
        return !heading.hasAttribute('data-gm-demoted');
      });

      /* Two headings is a document you can already see the whole of. The
         contents list stays hidden below that rather than adding a box
         that says less than the page does. */
      var panel = layout.querySelector('[data-gm-legal-contents]');
      if (!panel || indexable.length < 3) return;

      var mount = panel.querySelector('[data-gm-legal-contents-mount]');
      if (!mount) return;

      var links = build(mount, indexable);
      panel.hidden = false;

      /* The sidebar column is opened only now, once there is something to
         put in it — see greasy-legal.css §1. */
      layout.classList.add('gm-legal__layout--contents');

      var sync = syncPanel(panel);
      sync();
      DESKTOP.addEventListener('change', sync);

      track(links);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  /* The theme editor swaps a section's markup in place, which throws away
     the promoted headings and the list built from them. */
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
